import { UseQueryOptions } from '@tanstack/react-query';
import {
	deleteAllPath,
	deletePath,
	getAllByIdsPath,
	getAllPath,
	getAllStubsPath,
	getByIdPath,
	getFlowVersionsPath,
	getPagePath,
	getStubsByIdPath,
	IConfigObjectBase,
	ItemPage,
	saveAllPath,
	savePath,
} from 'ihub/common';
import { cacheStaleTime } from '../constants';
import HttpResponseOptions from '../interface/http-response-options';
import QueryClientWrapper from '../utils/create-query-client';
import {
	buildURL,
	getTenantID,
	handleResponse,
	standardDelete,
	standardGet,
	standardPost,
	validateOkResponse,
} from './http-helper-const';
import Parameter from './query-parameter';

export class CRUDHelperBase<T extends IConfigObjectBase> {
	protected objectType = '';

	constructor(objectType: string) {
		this.objectType = objectType;
	}

	getPageQuery(
		limit: number,
		offset: number,
		queryParams?: Parameter[]
	): UseQueryOptions<ItemPage<T>, Error, ItemPage<T>> {
		if (offset < 0) {
			offset = 0;
		}
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getPagePath,
				limit.toString(),
				offset.toString(),
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<ItemPage<T>> => {
				const url = buildURL(
					this.objectType,
					[getPagePath, limit.toString(), offset.toString()],
					queryParams
				);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get page', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getAllQuery(queryParams?: Parameter[]): UseQueryOptions<T[], Error, T[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getAllPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<T[]> => {
				const url = buildURL(this.objectType, [getAllPath], queryParams);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get all', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getAllByIdsQuery(Ids: string[], queryParams?: Parameter[]): UseQueryOptions<T[], Error, T[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getAllByIdsPath,
				...Ids,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<T[]> => {
				const url = buildURL(this.objectType, [getAllByIdsPath], queryParams);
				const res = await standardPost(url, Ids);
				await validateOkResponse(res, 'get all by IDs', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	invalidatedGetAllByIdsQuery(remove = false) {
		return QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getAllByIdsPath],
			remove
		);
	}

	getAllStubsQuery(queryParams?: Parameter[]): UseQueryOptions<T[], Error, T[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getAllStubsPath,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<T[]> => {
				const url = buildURL(this.objectType, [getAllStubsPath], queryParams);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get all stubs', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	invalidatedGetAllStubsQuery(remove = false) {
		return QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getAllStubsPath],
			remove
		);
	}

	getStubsByIdsQuery(Ids: string[], queryParams?: Parameter[]): UseQueryOptions<T[], Error, T[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getStubsByIdPath,
				...Ids,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<T[]> => {
				const url = buildURL(this.objectType, [getStubsByIdPath], queryParams);
				const res = await standardPost(url, Ids);
				await validateOkResponse(res, 'get stubs by IDs', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	getByID(
		Id: string,
		queryParams?: Parameter[],
		additionalHeaders?: HeadersInit
	): UseQueryOptions<T, Error, T> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getByIdPath,
				Id,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<T> => {
				const url = buildURL(this.objectType, [getByIdPath, Id], queryParams);
				const res = await standardGet(url, additionalHeaders);
				await validateOkResponse(res, 'get by ID', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async saveAll(
		items: T[],
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		shouldInvalidateKeys = true
	) {
		const url = buildURL(this.objectType, [saveAllPath], queryParams);
		const response = await standardPost(url, items);
		await handleResponse(response, 'save all', this.objectType, options);
		if (shouldInvalidateKeys) {
			await this.invalidateKeys();
		}
	}

	async save(
		item: T,
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		additionalHeaders?: HeadersInit,
		invalidateKeys = true
	) {
		const url = buildURL(this.objectType, [savePath], queryParams);
		const response = await standardPost(url, item, additionalHeaders);
		await handleResponse(response, 'save', this.objectType, options);
		if (invalidateKeys) {
			await this.invalidateKeys();
		}
	}

	async deleteAll(
		itemIds: string[],
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		remove = true
	) {
		const url = buildURL(this.objectType, [deleteAllPath], queryParams);
		const response = await standardDelete(url, itemIds);
		await handleResponse(response, 'delete all', this.objectType, options);
		await this.invalidateKeys(undefined, remove);
	}

	async delete(
		itemId: string,
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		additionalHeaders?: HeadersInit,
		remove = true
	) {
		const url = buildURL(this.objectType, [deletePath, itemId], queryParams);
		const response = await standardDelete(url, undefined, additionalHeaders);
		await handleResponse(response, 'delete', this.objectType, options);
		await this.invalidateKeys(undefined, remove);
	}

	async invalidateKeys(id?: string, remove = false) {
		await QueryClientWrapper.InvalidateKey([getTenantID(), this.objectType, getAllPath], remove);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getAllStubsPath],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getAllByIdsPath],
			remove
		);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getStubsByIdPath],
			remove
		);
		await QueryClientWrapper.InvalidateKey([getTenantID(), this.objectType, getPagePath], remove);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getFlowVersionsPath],
			remove
		);

		// removes either a single cached object by Id, or all
		if (id) {
			await QueryClientWrapper.InvalidateKey(
				[getTenantID(), this.objectType, getByIdPath, id],
				remove
			);
		} else {
			await QueryClientWrapper.InvalidateKey([getTenantID(), this.objectType, getByIdPath], remove);
		}
	}

	async invalidateGetPage(remove = false) {
		await QueryClientWrapper.InvalidateKey([getTenantID(), this.objectType, getPagePath], remove);
	}

	async invalidateGetByID(remove = false) {
		await QueryClientWrapper.InvalidateKey([getTenantID(), this.objectType, getByIdPath], remove);
	}
}
